import { Component, OnInit, HostListener, signal } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {  Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AlertService } from '../alert.service';
import { NgbModal, NgbTooltip, NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavContent, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap';
import { faAngleLeft, faSave, faTrashCan, faStar, faDownload, faTriangleExclamation, faPlus, faSortUp, faSortDown, faFilter, faExternalLinkAlt, faLock, faUnlock } from '@fortawesome/free-solid-svg-icons';
import { Location, NgIf, NgFor, CommonModule } from '@angular/common';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { TableControlService } from '../table-control.service';
import { DomSanitizer } from '@angular/platform-browser';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import dayjs from 'dayjs';
import { MultiYearAgreementService } from '@app-shared/services/multi-year-agreement.service';
import { AssignedProject, MultiYearAgreement } from '@app-shared/interfaces/multi-year-agreement/multi-year-agreement';
import { ProjectStatusPipe } from '@main/project-status.pipe';
import { ProjectStagePipe } from '@main/project-stage.pipe';
import { AssignProjectToMyaModalComponent } from '@app-shared/modals/assign-project-to-mya-modal/assign-project-to-mya-modal.component';
import { GenerateNextMyaProjectModalComponent } from '@app-shared/modals/generate-next-mya-project-modal/generate-next-mya-project-modal.component';
import { LoaderService } from '@app-shared/services/loader.service';
import { NgxSpinnerComponent } from 'ngx-spinner';

@Component({
  selector: 'app-multi-year-agreements-detail',
  templateUrl: './multi-year-agreements-detail.component.html',
  styleUrl: './multi-year-agreements-detail.component.css',
   providers: [ProjectStagePipe, ProjectStatusPipe],
  imports: [NgbTooltip, FaIconComponent, NgIf, ReactiveFormsModule, NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavContent, NgFor, RouterLink, NgbNavOutlet, CommonModule, ProjectStatusPipe, ProjectStagePipe, NgxSpinnerComponent]
})
export class MultiYearAgreementsDetailComponent  implements OnInit {
  //Screensize event Listener
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.resizeSubject.next((event.target as Window).innerWidth);
  }

  // Variable Subject
  private resizeSubject: Subject<number> = new Subject<number>();

  // Client
  myaForm: UntypedFormGroup;
  multiYearAgreement = signal<MultiYearAgreement | null>(null);
  myaId = signal<string | null>(null);
  multiYearAgreementProjects = signal<AssignedProject[]>([]);


  // Font Awesome Properties
  faAngleLeft = faAngleLeft;
  faSave = faSave;
  faTrashCan = faTrashCan;
  faStar = faStar;
  faDownload = faDownload;
  faTriangleExclamation = faTriangleExclamation;
  faPlus = faPlus;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faFilter = faFilter;
  faExternalLinkAlt = faExternalLinkAlt;
  faLock = faLock;
  faUnlock = faUnlock;

  // Properties
  isMobileView: boolean = false;
  currentYear = dayjs().year();
  isInputLocked = signal<boolean>(true);

  // Table Controls
  tableControls: TableControlService;

  // Get isMobile value in reactive way
  public get isMobile(): boolean {
    return this.isMobileView;
  }

  constructor(public location: Location,
    private route: ActivatedRoute,
    private multiYearAgreementService: MultiYearAgreementService,
    private alertService: AlertService,
    private modalService: NgbModal,
    private loaderService: LoaderService,
    public sanitizer: DomSanitizer) {
      this.resizeSubject.pipe(
        debounceTime(100)
      ).subscribe(width => {
        if (width <= 768) {
          this.isMobileView = true;
        } else {
          this.isMobileView = false;
        }
      });
     }

  ngOnInit(): void {
    this.myaId.set(this.route.snapshot.paramMap.get('myaId'));
    this.myaForm = new UntypedFormGroup({
      clientName: new UntypedFormControl(),
      site: new UntypedFormControl(''),
      startYear: new UntypedFormControl(this.currentYear),
      finalYear: new UntypedFormControl(),
      agreementLength: new UntypedFormControl(3),
      status: new UntypedFormControl(),
    });
    this.getMultiYearAgreementById();
    if (window.innerWidth <= 768) {
      this.isMobileView = true;
    } else {
      this.isMobileView = false;
    }
  }

  ngOnDestroy(): void {
    this.modalService.hasOpenModals() && this.modalService.dismissAll();
    this.resizeSubject.unsubscribe();
  }

  // Open Delete Mya Confirmation Modal
  openDeleteMyaConfirmationModal(): void {
    const confirmationModalRef = this.modalService.open(ConfirmationModalComponent);
    confirmationModalRef.componentInstance.message = "Are you sure you would like to delete this Multi-year Agreement?";
    confirmationModalRef.componentInstance.actionBtnTitle = "Delete";
    confirmationModalRef.componentInstance.confirmed.subscribe(() => {
      this.deleteMultiYearAgreement();
    });
  }

  // Get Client
  getMultiYearAgreementById(): void {
    this.loaderService.showSpinner();
    this.multiYearAgreementService.getMultiYearAgreementById(this.myaId()).subscribe((multiYearAgreement: MultiYearAgreement) => {
      this.multiYearAgreement.set(multiYearAgreement);
      this.multiYearAgreementProjects.set(multiYearAgreement.assignedProjects || []);
      this.setmyaFormValues(this.multiYearAgreement());
      this.loaderService.hideSpinner();
    });
  }

  // Set Client Form Values
  setmyaFormValues(myaData: any): void {
    const address = myaData?.siteAddress;
    const siteName = `${address?.street}, ${address?.city}, ${address?.county}, ${address?.state} ${address?.postalCode} ${myaData?.siteName || ''}`;
    this.myaForm.controls.clientName.setValue(myaData.clientName);
    this.myaForm.controls.site.setValue(siteName);
    this.myaForm.controls.startYear.setValue(myaData.startYear);
    this.myaForm.controls.agreementLength.setValue(myaData.length);
    this.myaForm.controls.finalYear.setValue(myaData.finalYear);
    this.myaForm.controls.status.setValue(myaData.status);

    //Disabled form
    this.myaForm.controls.clientName.disable();
    this.myaForm.controls.site.disable();
    this.myaForm.controls.startYear.disable();
    this.myaForm.controls.agreementLength.disable();
    this.myaForm.controls.finalYear.disable();
  }

  // Update Multi-Year Agreement
  updateMultiYearAgreement(): void {
    if (this.myaForm.valid) {
      const requestBody = {
        length: this.myaForm.getRawValue().agreementLength,
        startYear: this.myaForm.getRawValue().startYear,
        status: this.myaForm.getRawValue().status
      };
      this.multiYearAgreementService.updateMultiYearAgreement(this.myaId(), requestBody).subscribe(() => {
        this.alertService.showSuccessAlert('Multi-Year Agreement Updated');
        this.isInputLocked.set(true);
        this.getMultiYearAgreementById();
      });
    } else {
      this.myaForm.markAllAsTouched();
    }
  }

  // Delete Multi-Year Agreement
  deleteMultiYearAgreement(): void {
    this.multiYearAgreementService.deleteMultiYearAgreement(this.myaId()).subscribe(() => {
      this.location.back();
    });
  }


  // Toogle Lock/Unlock
  toggleLockUnlock(): void {
    this.isInputLocked.set(!this.isInputLocked());
    if(this.isInputLocked()) {
      this.setmyaFormValues(this.multiYearAgreement());
    } else {
      this.myaForm.controls.startYear.enable();
      this.myaForm.controls.agreementLength.enable();
    }
  }

  openAssignProjectToMyaModal() {
    console.log('multiYearAgreement', this.multiYearAgreement());
      const modalRef = this.modalService.open(AssignProjectToMyaModalComponent);
      modalRef.componentInstance.myaId.set(this.multiYearAgreement()?.id);
      modalRef.componentInstance.clientId.set(this.multiYearAgreement()?.clientId);
      modalRef.componentInstance.siteId.set(this.multiYearAgreement()?.siteId);
      modalRef.componentInstance.myaProjectYear.set(this.multiYearAgreement()?.startYear);
      modalRef.componentInstance.type.set("MYA_DETAIL");
      modalRef.componentInstance.okay.subscribe(() => {
        this.getMultiYearAgreementById();
      });
    }


  // Open Generate Next MYA Project Modal
  openGenerateNextMyaProjectModal(): void {
    // Get latest assignedProject in multiYearAgreement
    const latestProject = this.multiYearAgreement().assignedProjects.reduce((latest, current) => {
      return (current.projectYear > latest.projectYear) ? current : latest;
    }, this.multiYearAgreement().assignedProjects.slice()[0]);
    const modalRef = this.modalService.open(GenerateNextMyaProjectModalComponent);
    modalRef.componentInstance.myaId.set(this.multiYearAgreement()?.id);
    modalRef.componentInstance.projectName.set(latestProject.projectName);
    modalRef.componentInstance.projectYear.set(latestProject.projectYear);
    modalRef.componentInstance.okay.subscribe(() => {
      this.getMultiYearAgreementById();
    });
  }

  //Disabled Generate Next MYA Project Button
  get disabledGenerateNextMyaProjectButton(): boolean {
    let hasMyaProjectYearEqualToLength = this.multiYearAgreementProjects().some(project => project.myaProjectYear === this.multiYearAgreement()?.length);
    let hasProjectYearEqualToFinalYear = this.multiYearAgreementProjects().some(project => project.projectYear === this.multiYearAgreement()?.finalYear);
    return this.multiYearAgreementProjects().length === this.multiYearAgreement()?.length && hasMyaProjectYearEqualToLength && hasProjectYearEqualToFinalYear;
  }

  /* ----- Form Accessors ----- */

  get clientName() { return this.myaForm.controls.name; }
  get site() { return this.myaForm.controls.type; }
  get startYear() { return this.myaForm.controls.leadType; }
  get agreementLength() { return this.myaForm.controls.leadType; }
  get status() { return this.myaForm.controls.status; }
}

